import api from "../services/api";

/** Optimized list for Campaigns grid (lean payload + cached stats). */
export const getCampaignList = async (params = {}) => {
  return api.get("/api/campaigns/list", { params });
};

/** Refresh stats only for one campaign card. */
export const refreshCampaignCardStats = async (campaignId) => {
  return api.post(`/api/campaigns/${campaignId}/card-stats/refresh`);
};

// Get single campaign by ID
export const getCampaignById = async (campaignId) => {
  return api.get(`/api/campaigns/${campaignId}`);
};

// Create new campaign
// File fields (csvFile, headerMediaFile) are appended as-is so multer can
// stream them; everything else is JSON-stringified or stringified.
export const createCampaign = async (campaignData) => {
  const formData = new FormData();
  const fileFields = new Set(["csvFile", "headerMediaFile"]);

  Object.keys(campaignData).forEach((key) => {
    const value = campaignData[key];

    if (fileFields.has(key)) {
      if (value) {
        formData.append(key, value);
      }
      return;
    }

    let normalized = value;

    if (typeof normalized === "boolean") {
      normalized = normalized.toString();
    }

    if (normalized === null || normalized === undefined) {
      normalized = "";
    }

    if (typeof normalized === "object") {
      formData.append(key, JSON.stringify(normalized));
    } else {
      formData.append(key, normalized);
    }
  });

  return api.post("/api/campaigns", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Update campaign
export const updateCampaign = async (campaignId, campaignData) => {
  return api.put(`/api/campaigns/${campaignId}`, campaignData);
};

// Delete campaign
export const deleteCampaign = async (campaignId) => {
  return api.delete(`/api/campaigns/${campaignId}`);
};

// Get campaign contacts
export const getCampaignContacts = async (campaignId, filters = {}) => {
  return api.get(`/api/campaigns/${campaignId}/contacts`, {
    params: filters,
  });
};

// Send campaign manually
export const sendCampaign = async (campaignId) => {
  return api.post(`/api/campaigns/${campaignId}/send`, {});
};

// Resume campaign - continue sending to pending contacts
export const resumeCampaign = async (campaignId) => {
  return api.post(`/api/campaigns/${campaignId}/resume`, {});
};

// Get campaign analytics
export const getCampaignAnalytics = async (campaignId) => {
  return api.get(`/api/campaigns/${campaignId}/analytics`);
};

// Force-recompute campaign analytics (drops snapshot cache, re-reads MySQL)
export const recomputeCampaignAnalytics = async (campaignId) => {
  return api.post(`/api/campaigns/${campaignId}/analytics/recompute`, {});
};

// Get campaign analytics summary with templates and phone info (FAST)
export const getCampaignAnalyticsSummary = async (campaignId) => {
  return api.get(`/api/campaigns/${campaignId}/analytics/summary`);
};

// Download campaign message logs as CSV
export const downloadCampaignLogs = async (campaignId) => {
  return api.get(`/api/campaigns/${campaignId}/logs/download`, {
    responseType: 'blob', // Important: handle CSV as blob
  });
};

// Abort campaign (instant or scheduled)
export const abortCampaign = async (campaignId) => {
  return api.post(`/api/campaigns/${campaignId}/abort`, {});
};

// Reschedule a cancelled/aborted campaign
export const rescheduleCampaign = async (campaignId, scheduledTimestamp, timezone) => {
  return api.post(`/api/campaigns/${campaignId}/reschedule`, {
    scheduledTimestamp,
    timezone,
  });
};

// Update scheduled campaign time
export const updateCampaignSchedule = async (campaignId, scheduledTimestamp, timezone) => {
  return api.put(`/api/campaigns/${campaignId}/schedule`, {
    scheduledTimestamp,
    timezone,
  });
};

// Change WhatsApp account for campaign
export const changeCampaignWhatsappAccount = async (campaignId, whatsappAccountId) => {
  return api.put(`/api/campaigns/${campaignId}/whatsapp-account`, {
    whatsapp_account_id: whatsappAccountId,
  });
};

// Validate campaign
export const validateCampaign = async (campaignId) => {
  return api.get(`/api/campaigns/${campaignId}/validate`);
};

// Get campaign sending status
export const getCampaignSendingStatus = async (campaignId) => {
  return api.get(`/api/campaigns/${campaignId}/sending-status`);
};

// Cancel scheduled campaign
export const cancelScheduledCampaign = async (campaignId) => {
  return api.delete(`/api/campaigns/${campaignId}/schedule`);
};

// Get scheduled campaign status
export const getScheduledCampaignStatus = async (campaignId) => {
  return api.get(`/api/campaigns/${campaignId}/schedule`);
};

// Send test template message to a single phone number
export const sendTestTemplate = async (testData) => {
  const WABA_API_BASE = process.env.REACT_APP_WABA_SERVER_URL || process.env.REACT_APP_API_URL;
  return api.post(`${WABA_API_BASE}/api/broadcast/send-template`, testData);
};

// Prepare retargeting - get available variables and valid templates
export const prepareRetargeting = async (campaignId, phoneId) => {
  return api.get(`/api/campaigns/${campaignId}/retarget/prepare`, {
    params: { phone_id: phoneId },
  });
};

// Retarget campaign
// When `headerMediaFile` is supplied we send the request as multipart/form-data
// so multer can stream the file. Otherwise we keep the JSON path unchanged so
// existing retarget calls aren't affected.
export const retargetCampaign = async (campaignId, retargetData) => {
  const hasHeaderMedia = retargetData && retargetData.headerMediaFile instanceof File;

  if (!hasHeaderMedia) {
    const { headerMediaFile: _omit, ...jsonPayload } = retargetData || {};
    return api.post(`/api/campaigns/${campaignId}/retarget`, jsonPayload);
  }

  const formData = new FormData();
  Object.keys(retargetData).forEach((key) => {
    const value = retargetData[key];

    if (key === "headerMediaFile") {
      if (value) formData.append(key, value);
      return;
    }

    if (value === null || value === undefined) return;

    if (Array.isArray(value) || typeof value === "object") {
      formData.append(key, JSON.stringify(value));
      return;
    }

    formData.append(key, typeof value === "boolean" ? String(value) : value);
  });

  return api.post(`/api/campaigns/${campaignId}/retarget`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Get date-wise analytics for campaign (for graphs)
export const getCampaignDateWiseAnalytics = async (campaignId, days = 30) => {
  return api.get(`/api/campaigns/${campaignId}/analytics/date-wise`, {
    params: { days },
  });
};

// Get contacts by status with pagination
export const getCampaignContactsByStatus = async (campaignId, status, page = 1, limit = 50) => {
  return api.get(`/api/campaigns/${campaignId}/contacts/by-status`, {
    params: { status, page, limit },
  });
};

// Export contacts by status as CSV (streaming download)
export const exportCampaignContactsByStatus = async (campaignId, status) => {
  return api.get(`/api/campaigns/${campaignId}/contacts/export`, {
    params: { status },
    responseType: 'blob',
  });
};

